import { Directive, ElementRef, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { TalkMeService } from '@main/online-chats/feature';
import { UrlNavigatorService } from '@shared/services';
import { WindowRef } from '@spartacus/core';

const MESSENGER_LINK_PATTERN = /^https?:\/\/(www\.)?(t\.me|max\.ru)\//;

/**
 * Позволяет перехватить клик по ссылке в innerHTML и перенаправить по роутингу
 */

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[processLinks]',
  standalone: false,
})
export class ProcessLinkDirective implements OnInit, OnDestroy {
  private clickListeners: (() => void)[] = [];

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private urlNavigatorService: UrlNavigatorService,
    private winRef: WindowRef,
    private talkMeService: TalkMeService,
  ) {}

  ngOnInit(): void {
    this.processLinks(this.el.nativeElement);
  }

  ngOnDestroy(): void {
    this.unsubscribeClickListeners();
  }

  private processLinks(element: HTMLElement): void {
    const links = Array.from(element.getElementsByTagName('a'));

    if (element.tagName === 'A') {
      links.push(element as HTMLAnchorElement);
    }

    for (let i = 0; i < links.length; i++) {
      const link = links[i];
      const href = link.getAttribute('href');

      if (!href) {
        continue;
      }

      if (MESSENGER_LINK_PATTERN.test(href)) {
        const messengerClickListener = this.renderer.listen(link, 'click', () => {
          void this.talkMeService.notifyOperatorAboutMessengerClick();
        });
        this.clickListeners.push(messengerClickListener);
        continue;
      }

      if (!this.isExcludeLink(href)) {
        if (!href.startsWith('?')) {
          const hrefWithoutSlash = href.startsWith('/') ? href.slice(1) : href;
          const newHref = `${this.winRef.location.origin}/${hrefWithoutSlash}`;

          this.renderer.setAttribute(link, 'href', newHref);
        }

        const clickListener = this.renderer.listen(link, 'click', (event: MouseEvent) => {
          event.preventDefault();
          const navigateHref = this.resolveNavigateHref(href);
          this.urlNavigatorService.navigate(navigateHref);
        });

        this.clickListeners.push(clickListener);
      }
    }
  }

  private unsubscribeClickListeners(): void {
    this.clickListeners.forEach((listener) => listener());
    this.clickListeners = [];
  }

  /**
   * Возвращает положительное значение в случае,
   * если переданная ссылка начинается с протокола
   * или является якорем.
   * Пример: http:, https:, tel:, mailto:, # и т.д
   */
  private isExcludeLink(href: string): boolean {
    const regex = /^(\w+):[\S]+/g;
    const isProtocol = regex.test(href);

    return href.startsWith('#') || isProtocol;
  }

  private resolveNavigateHref(href: string): string {
    if (!href.startsWith('?')) {
      return href;
    }

    const currentQuery = new URLSearchParams(this.winRef.location.search || '');
    const incomingQuery = new URLSearchParams(href.slice(1));

    incomingQuery.forEach((value, key) => {
      currentQuery.set(key, value);
    });

    const queryString = currentQuery.toString();
    const pathname = this.winRef.location.pathname || '/';

    return queryString ? `${pathname}?${queryString}` : pathname;
  }
}
